import React, { useCallback } from 'react';
import { AlertTriangle, CheckCircle, Info, X, XCircle } from 'lucide-react';
import { useSnackbar } from 'notistack';
import type { CustomContentProps } from 'notistack';

const variantStyles = {
  success:
    'border-green-600 bg-white text-green-800 ring-green-100 dark:bg-slate-900 dark:text-green-100 dark:ring-green-800/60',
  error:
    'border-red-600 bg-white text-red-800 ring-red-100 dark:bg-slate-900 dark:text-red-100 dark:ring-red-800/60',
  info: 'border-blue-600 bg-white text-blue-800 ring-blue-100 dark:bg-slate-900 dark:text-blue-100 dark:ring-blue-800/60',
  warning:
    'border-amber-600 bg-white text-amber-800 ring-amber-100 dark:bg-slate-900 dark:text-amber-100 dark:ring-amber-800/60',
};

const variantIcons = {
  success: <CheckCircle className="h-5 w-5" />,
  error: <XCircle className="h-5 w-5" />,
  info: <Info className="h-5 w-5" />,
  warning: <AlertTriangle className="h-5 w-5" />,
};

export const AppToast = React.forwardRef<HTMLDivElement, CustomContentProps>(
  ({ action, id, message, style, variant }, ref) => {
    const { closeSnackbar } = useSnackbar();
    const toastVariant =
      variant === 'success' || variant === 'error' || variant === 'info' || variant === 'warning'
        ? variant
        : 'info';
    const toastAction = typeof action === 'function' ? action(id) : action;

    const handleClose = useCallback(() => {
      closeSnackbar(id);
    }, [closeSnackbar, id]);

    return (
      <div
        ref={ref}
        style={style}
        className={`flex min-w-[320px] max-w-[440px] items-start gap-3 rounded-md border-l-4 px-4 py-3 shadow-xl ring-1 ${variantStyles[toastVariant]}`}
      >
        <div className="mt-0.5 flex-shrink-0">{variantIcons[toastVariant]}</div>
        <div className="min-w-0 flex-1 break-words text-sm font-medium leading-5">{message}</div>
        {toastAction && (
          <div className="flex flex-shrink-0 items-center gap-2">{toastAction}</div>
        )}
        <button
          type="button"
          onClick={handleClose}
          className="mt-0.5 flex-shrink-0 rounded p-0.5 opacity-70 transition hover:bg-black/5 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current dark:hover:bg-white/10"
          aria-label="Close toast"
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
);

AppToast.displayName = 'AppToast';
